import { useEffect } from "react";

export default function TermsAndConditions() {
  useEffect(() => {
    document.title = "Terms and Conditions | Truestays";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Truestays terms and conditions of service for property owners, guests and referral partners.");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://www.truestays.co.uk/terms-and-conditions";
  }, []);

  return (
    <div style={{ background: "#F7F7F7", minHeight: "100vh" }}>
      {/* Hero */}
      <div style={{
        background: "#f4f2f0",
        padding: "100px clamp(20px, 5vw, 80px) 64px",
        textAlign: "center",
      }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>
          Legal
        </p>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "clamp(32px, 4vw, 52px)", color: "#1A1A1A", margin: "0 0 16px", letterSpacing: "-0.03em", lineHeight: 1.08 }}>
          Terms & Conditions
        </h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#555", maxWidth: 480, margin: "0 auto", lineHeight: 1.65 }}>
          Last updated: June 2026
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "64px clamp(20px, 5vw, 40px) 80px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "48px clamp(24px, 4vw, 56px)", border: "1px solid #E5E5E5" }}>

          <Section title="1. Introduction">
            <p>These Terms and Conditions ("Terms") govern your use of the services provided by Truestays Group Ltd. ("Truestays", "we", "our", or "us"), a company registered in England and Wales, trading as Truestays. By engaging our services or using our website at truestays.co.uk, you agree to be bound by these Terms.</p>
            <p>Please read these Terms carefully. If you do not agree with any part, you should not use our services.</p>
          </Section>

          <Section title="2. Services">
            <p>Truestays provides short-let and serviced accommodation management services to property owners ("Hosts") across the United Kingdom. Our services include, but are not limited to:</p>
            <ul>
              <li>Property listing and marketing across short-let platforms</li>
              <li>Dynamic pricing and revenue management</li>
              <li>Guest communication and booking management</li>
              <li>Housekeeping and linen coordination</li>
              <li>Maintenance coordination</li>
              <li>Monthly performance reporting</li>
            </ul>
            <p>The exact scope of services shall be set out in your individual Management Agreement.</p>
          </Section>

          <Section title="3. Management Agreement">
            <p>A separate Management Agreement will be entered into between Truestays and each Host prior to the commencement of services. The Management Agreement shall take precedence over these Terms in the event of any conflict. These Terms are incorporated by reference into all Management Agreements.</p>
          </Section>

          <Section title="4. Fees and Payment">
            <p>Truestays charges a management fee as a percentage of gross booking revenue, as specified in your Management Agreement (starting from 12% + VAT). All fees are subject to VAT at the prevailing rate.</p>
            <p>Payouts to Hosts are made on a per-booking basis, following guest check-out and deduction of applicable fees and platform commissions. Truestays reserves the right to withhold payouts in cases of guest disputes, chargebacks, or outstanding cleaning/maintenance charges.</p>
          </Section>

          <Section title="5. Host Obligations">
            <p>As a Host, you agree to:</p>
            <ul>
              <li>Ensure the property complies with all applicable laws, regulations, and safety standards</li>
              <li>Hold adequate buildings and contents insurance, and short-let specific liability insurance</li>
              <li>Notify Truestays of any mortgage, lease, or planning restrictions that may affect short-letting</li>
              <li>Maintain the property in a clean, safe, and well-maintained condition</li>
              <li>Not directly solicit or accept bookings from guests introduced by Truestays during the term and for 12 months thereafter</li>
              <li>Provide accurate information about the property at all times</li>
            </ul>
          </Section>

          <Section title="6. Termination">
            <p>Either party may terminate the Management Agreement by providing written notice as specified in that agreement (typically 30 days). Truestays reserves the right to terminate immediately in cases of Host breach, misrepresentation, or conduct that may harm our reputation or guests.</p>
            <p>Upon termination, Truestays will fulfil any existing confirmed bookings unless otherwise agreed. All outstanding fees remain payable upon termination.</p>
          </Section>

          <Section title="7. Liability">
            <p>Truestays acts as an agent on behalf of Hosts and is not liable for any damage, loss, or injury arising from the use of a Host's property by guests, except where caused by our gross negligence or wilful misconduct.</p>
            <p>Our total aggregate liability to you in connection with our services shall not exceed the total management fees paid by you in the three months preceding the claim.</p>
            <p>We do not exclude liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.</p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>All content created by Truestays in connection with your property listing - including photography, copy, and descriptions - remains the intellectual property of Truestays until full payment has been received. You grant Truestays a non-exclusive licence to use property images and information for marketing purposes during the term of the agreement.</p>
          </Section>

          <Section title="9. Data Protection">
            <p>Truestays processes personal data in accordance with our Privacy Policy and applicable UK data protection legislation, including the UK GDPR and the Data Protection Act 2018. By engaging our services, you consent to the processing of your personal data as described in our Privacy Policy.</p>
          </Section>

          <Section title="10. Governing Law">
            <p>These Terms and any disputes arising from them shall be governed by and construed in accordance with the laws of England and Wales. The courts of England and Wales shall have exclusive jurisdiction to resolve any disputes.</p>
          </Section>

          <Section title="11. Changes to These Terms">
            <p>Truestays reserves the right to update these Terms at any time. We will notify existing clients of material changes via email. Continued use of our services following notification constitutes acceptance of the updated Terms.</p>
          </Section>

          <Section title="12. Contact">
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p><strong>Truestays Group Ltd.</strong> (trading as Truestays)<br />
            Email: <a href="mailto:hello@truestays.co.uk" style={{ color: "#4A6DC7", textDecoration: "none" }}>hello@truestays.co.uk</a></p>
          </Section>

        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", marginBottom: 12, marginTop: 0 }}>{title}</h2>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#4A4A4A", lineHeight: 1.75 }}>
        {children}
      </div>
      <style>{`
        div[style*="margin-bottom: 36px"] p { margin: 0 0 12px; }
        div[style*="margin-bottom: 36px"] ul { padding-left: 20px; margin: 0 0 12px; }
        div[style*="margin-bottom: 36px"] li { margin-bottom: 6px; }
      `}</style>
    </div>
  );
}